import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';
import { Loader } from './ui/Loader';
import { supabase } from '../utils/supabase/client';
import { toast } from 'sonner';
import { devError } from '../utils/logger';

interface UpgradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>; // ✅ FIX: Changed to async
  planName: string;
  planType: string;
  priceId: string;
  monthlyPrice: number;
}

export function UpgradeConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  planName,
  planType,
  priceId,
  monthlyPrice
}: UpgradeConfirmationModalProps) {
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false); // ✅ FIX: Loading state for confirmation button
  const [preview, setPreview] = useState<{
    amountDueNow: number;
    nextInvoiceAmount: number;
    currency: string;
    periodEnd: string | null;
    breakdown?: {
      currentMonthlyPrice: number;
      newMonthlyPrice: number;
      daysRemaining: number;
      daysUsed: number; // ✅ NEW: Days used for better UX
      totalPeriodDays: number;
      creditAmount: number;
      newProrationAmount: number;
    };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ✅ FIX: Determine if this is a business purchase (employer plans)
  const isBusinessPurchase = planType.includes('arbeidsgiver');

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
    } else {
      // Reset state when modal closes
      setLoading(true);
      setPreview(null);
      setError(null);
    }
  }, [isOpen, planType, monthlyPrice]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Du må være logget inn');
        setLoading(false);
        return;
      }

      // Nexi does not provide Stripe-style proration previews. We default to a robust policy:
      // plan change takes effect at next renewal (amount due now is 0).
      setPreview({
        amountDueNow: 0,
        nextInvoiceAmount: monthlyPrice,
        currency: 'NOK',
        periodEnd: null,
      });
    } catch (err: any) {
      devError('❌ Preview fetch error:', err);
      setError(err.message || 'Noe gikk galt');
      toast.error(err.message || 'Kunne ikke hente forhåndsvisning');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setUpgradeLoading(true);
    onConfirm().then(() => {
      onClose();
    }).catch((err) => {
      devError('❌ Upgrade confirmation error:', err);
      setError(err.message || 'Noe gikk galt under oppgraderingen');
      toast.error(err.message || 'Noe gikk galt under oppgraderingen');
    }).finally(() => {
      setUpgradeLoading(false);
    });
  };

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div key="upgrade-confirmation-modal" className="fixed inset-0" style={{ zIndex: 'var(--z-overlay)' }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            style={{ zIndex: 'var(--z-modal)' }}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#FF6B35] to-[#F7931E] p-6 text-white">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-2xl font-bold">Oppgrader abonnement</h2>
              <p className="text-white/90 mt-1">Bekreft oppgraderingen din</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader size="lg" className="text-[#FF6B35] mb-3" />
                  <p className="text-gray-600">Beregner pris...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
                  <p className="text-red-600 text-center">{error}</p>
                  <button
                    onClick={fetchPreview}
                    className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Prøv igjen
                  </button>
                </div>
              ) : preview ? (
                <div className="space-y-4">
                  {/* Plan Info */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#FF6B35] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900">
                          Oppgraderer til <span className="text-[#FF6B35]">{planName}</span>
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Fremtidig månedlig pris: <strong>{monthlyPrice} kr/mnd</strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Details */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {/* ✅ IMPROVED UX: Show days used and better breakdown */}
                    {preview.breakdown && preview.breakdown.daysRemaining > 0 ? (
                      <div className="space-y-2 mb-3 pb-3 border-b border-gray-300">
                        {/* ✅ NEW: Show days used for context */}
                        {preview.breakdown.daysUsed > 0 && (
                          <div className="flex justify-between items-center text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mb-2">
                            <span className="text-blue-700">Dager brukt på nåværende plan:</span>
                            <span className="font-medium text-blue-900">{preview.breakdown.daysUsed} av {preview.breakdown.totalPeriodDays} dager</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Refusjon for {preview.breakdown.daysRemaining} ubrukte dager:</span>
                          <span className="text-green-600 font-medium">-{preview.breakdown.creditAmount.toFixed(2)} kr</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">
                            Nytt abonnement for {preview.breakdown.daysRemaining} dager:
                          </span>
                          <span className="text-orange-600 font-medium">+{(preview.amountDueNow + preview.breakdown.creditAmount).toFixed(2)} kr</span>
                        </div>
                      </div>
                    ) : null}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-medium">Totalt å betale nå:</span>
                      <span className="font-bold text-xl text-gray-900">
                        {preview.amountDueNow.toFixed(2)} kr
                      </span>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                      <p className="text-sm text-gray-600">
                        Du oppgraderer til {planName} ({monthlyPrice} kr/mnd). Endringen trer i kraft ved neste fornyelse.
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Abonnementet fornyes automatisk hver måned til du avbryter. Du kan avslutte når som helst under Innstillinger → Abonnement.
                      </p>
                      {preview.periodEnd && (
                        <p className="text-sm text-gray-500 mt-2">
                          Neste faktura: {new Date(preview.periodEnd).toLocaleDateString('no-NO', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} for {monthlyPrice} kr
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ✅ FIXED: Legal Notice - Dynamic based on plan type */}
                  {isBusinessPurchase ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-xs text-orange-900">
                        <strong>Næringskjøp:</strong> Som verifisert bedrift har du ikke lovfestet angrefrist. 
                        Avtalen er bindende. Oppgraderingen trer i kraft ved neste fornyelse.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-900">
                        <strong>Digital tjeneste:</strong> Oppgraderingen leveres umiddelbart. Angreretten bortfaller ved samtykke til umiddelbar levering (jf. Angrerettloven § 22 n). 
                        Oppgraderingen trer i kraft ved neste fornyelse.
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={onClose}
                      disabled={upgradeLoading}
                      className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Avbryt
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={upgradeLoading}
                      className={`flex-1 px-4 py-3 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                        upgradeLoading
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white hover:shadow-lg'
                      }`}
                    >
                      {upgradeLoading ? (
                        <>
                          <Loader size="sm" className="text-white" />
                          Oppgraderer...
                        </>
                      ) : (
                        'Bekreft oppgradering'
                      )}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
